// client/src/components/pages/Settings/DatabaseSettings.tsx (converted from .jsx, ST-00222)
//
// Settings → Database → Performance: a read-only view of the ticket tables' indexes
// and sizes (global admins). Audit IAM-19 / PLT-15: "Rebuild Performance Indexes" ran a
// SQL Server script and never worked (and failed CSRF before that), the progress log
// and the promised speed-ups went with it, and the index counts are no longer compared
// with a fixed list of SQL-Server-era names ("Incomplete / Needs rebuild"). Indexes are
// created by the database migrations on every deploy.
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import { Database, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import BreadcrumbsJs from '../../common/Breadcrumbs';
import type {
  IndexStatus,
  SettingsApiError,
  TableIndexes,
} from '../../../types/clientPages/tenantManagement';
import './SettingsDetail.css';

const Breadcrumbs = BreadcrumbsJs as unknown as ComponentType<{
  items: Array<{ label: string; path?: string }>;
}>;

const formatCount = (v: number | string | null | undefined): string =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString();

const TableIndexCard = ({
  title,
  table,
  tone,
}: {
  title: string;
  table: TableIndexes;
  tone: 'accent' | 'success';
}) => (
  <div
    style={{
      background: `var(--${tone}-soft)`,
      padding: '1rem',
      borderRadius: '0.625rem',
      border: `1px solid var(--${tone}-border)`,
    }}
  >
    <h4
      style={{
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: `var(--${tone}-text)`,
        margin: '0 0 0.375rem 0',
      }}
    >
      {title} Indexes
    </h4>
    <p
      style={{
        fontSize: '1.75rem',
        fontWeight: 700,
        color: `var(--${tone})`,
        margin: '0 0 0.5rem 0',
      }}
    >
      {table.found}
    </p>
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.8125rem' }}>
      {table.indexes.map((ix) => (
        <li
          key={ix.index_name}
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.2rem 0' }}
        >
          <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{ix.index_name}</span>
          <span style={{ color: 'var(--text-faint)' }}>{ix.IndexType}</span>
          {ix.IsUnique && (
            <span
              style={{
                fontSize: '0.6875rem',
                padding: '0 0.375rem',
                borderRadius: '0.25rem',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
              }}
            >
              unique
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const DatabaseSettings = () => {
  const [dbIndexStatus, setDbIndexStatus] = useState<IndexStatus | null>(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState('');

  const loadDatabaseStatus = useCallback(async () => {
    try {
      setDbLoading(true);
      setDbError('');
      const response = await api.database.getIndexStatus();
      setDbIndexStatus((response.data as { data?: IndexStatus }).data ?? null);
    } catch (error) {
      console.error('Error loading database status:', error);
      const err = error as SettingsApiError;
      setDbError(
        'Failed to load database status: ' +
          (err.response?.data?.message || err.message || 'unknown error')
      );
    } finally {
      setDbLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDatabaseStatus();
  }, [loadDatabaseStatus]);

  const breadcrumbItems = [{ label: 'Modules' }, { label: 'Database' }, { label: 'Performance' }];
  const sourceData = dbIndexStatus?.tableStats?.sourceData ?? null;
  const amendmentData = dbIndexStatus?.tableStats?.amendmentData ?? null;

  return (
    <div className="settings-detail">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="settings-detail-container">
        {/* Header */}
        <div className="settings-detail-header">
          <div className="settings-detail-header-icon">
            <Database size={26} />
          </div>
          <div>
            <h1 className="settings-detail-title">Database Performance</h1>
            <p className="settings-detail-description">
              Indexes and size of the ticket tables (read-only). Indexes are created by the database
              migrations on every deploy.
            </p>
          </div>
        </div>

        {/* Alerts */}
        {dbError && (
          <div className="settings-alert settings-alert-error" role="alert">
            {dbError}
          </div>
        )}

        {/* Content */}
        {dbLoading ? (
          <div className="settings-loading">
            <div className="settings-spinner"></div>
            <p>Loading database status...</p>
          </div>
        ) : (
          <div className="settings-detail-content">
            {dbIndexStatus && (
              <div className="settings-card-detail">
                <div className="settings-card-header-detail">
                  <Database size={20} />
                  <h2>Current Indexes</h2>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1.25rem',
                  }}
                >
                  <TableIndexCard
                    title="LexiBridgeSource"
                    table={dbIndexStatus.sourceIndexes}
                    tone="accent"
                  />
                  <TableIndexCard
                    title="LexiBridgeAmendments"
                    table={dbIndexStatus.amendmentIndexes}
                    tone="success"
                  />
                </div>

                {/* Table Statistics */}
                {(sourceData || amendmentData) && (
                  <div
                    style={{
                      background: 'var(--surface-sunken)',
                      padding: '1.25rem',
                      borderRadius: '0.75rem',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <h4
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'var(--text-strong)',
                        margin: '0 0 1rem 0',
                      }}
                    >
                      Table Statistics
                    </h4>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '1.5rem',
                        fontSize: '0.875rem',
                      }}
                    >
                      {sourceData && (
                        <div>
                          <p style={{ fontWeight: 500, margin: '0 0 0.5rem 0' }}>
                            LexiBridgeSource:
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Records: {formatCount(sourceData.record_count)}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Dispatch Locations: {formatCount(sourceData.unique_dispatch_locations)}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Receipt Locations: {formatCount(sourceData.unique_receipt_locations)}
                          </p>
                        </div>
                      )}
                      {amendmentData && (
                        <div>
                          <p style={{ fontWeight: 500, margin: '0 0 0.5rem 0' }}>
                            LexiBridgeAmendments:
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Total Amendments: {formatCount(amendmentData.total_amendments)}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Active Amendments: {formatCount(amendmentData.active_amendments)}
                          </p>
                          <p style={{ margin: '0.25rem 0' }}>
                            Amended Tickets: {formatCount(amendmentData.amended_tickets)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="settings-form-actions">
              <button
                type="button"
                onClick={loadDatabaseStatus}
                disabled={dbLoading}
                className="settings-btn settings-btn-secondary"
              >
                <RefreshCw size={18} />
                Refresh Status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseSettings;
